/**
 * A rough monthly cost range for a project's stack (see stack-files.ts), so
 * people can see what a tier is likely to cost before they create it.
 *
 * Prices are a dated snapshot of the cloud's list prices — not live — and the
 * UI shows `pricesAsOf` with a link to the official calculator.
 *
 * SUPERSEDED (ENG-443): these hard-coded prices are kept only for the existing
 * stack card. Live, per-region prices come from gcp-pricing.ts and the API's
 * gcpPricingService (GET /pricing/gcp). Never present this snapshot as live.
 */
import { stackEnvSettings } from "./stack-files.js";
export const STACK_PRICING = {
    gcp: {
        pricesAsOf: "2026-05-11",
        calculatorUrl: "https://cloud.google.com/products/calculator",
        currency: "USD",
        cloudRunVcpuSecond: 0.000024,
        cloudRunGibSecond: 0.0000025,
        cloudRunPerMillionRequests: 0.4,
        freeTier: { vcpuSeconds: 180_000, gibSeconds: 360_000, requests: 2_000_000 },
        artifactRegistryGbMonth: 0.1,
        storageGbMonth: 0.02,
    },
};
/** What each running instance is given (Cloud Run's defaults; the Terraform doesn't change them). */
const INSTANCE_VCPU = 1;
const INSTANCE_GIB = 0.5;
const MONTH_SECONDS = 30 * 24 * 60 * 60;
/** Light traffic: an app that is mostly idle and scales to zero. */
const LOW_REQUESTS = 10_000;
const LOW_REQUEST_SECONDS = 0.2;
const LOW_IMAGES_GB = 0.5;
/** Busy: every allowed instance working for a share of the month. */
const HIGH_BUSY_SHARE = 0.25;
const HIGH_REQUESTS_PER_SECOND = 20;
const HIGH_IMAGES_GB = 2;
const STATE_GB = 0.01;
function lowUsage() {
    const busySeconds = LOW_REQUESTS * LOW_REQUEST_SECONDS;
    return {
        vcpuSeconds: busySeconds * INSTANCE_VCPU,
        gibSeconds: busySeconds * INSTANCE_GIB,
        requests: LOW_REQUESTS,
        imagesGb: LOW_IMAGES_GB,
    };
}
function highUsage(maxInstances) {
    const busySeconds = maxInstances * MONTH_SECONDS * HIGH_BUSY_SHARE;
    return {
        vcpuSeconds: busySeconds * INSTANCE_VCPU,
        gibSeconds: busySeconds * INSTANCE_GIB,
        requests: busySeconds * HIGH_REQUESTS_PER_SECOND,
        imagesGb: HIGH_IMAGES_GB,
    };
}
/** Cost of one environment, using up what is left of the shared free tier. */
function cost(usage, free, p) {
    const take = (key) => {
        const covered = Math.min(usage[key], free[key]);
        free[key] -= covered;
        return usage[key] - covered;
    };
    return (take("vcpuSeconds") * p.cloudRunVcpuSecond +
        take("gibSeconds") * p.cloudRunGibSecond +
        (take("requests") / 1_000_000) * p.cloudRunPerMillionRequests +
        usage.imagesGb * p.artifactRegistryGbMonth +
        STATE_GB * p.storageGbMonth);
}
/**
 * A low–high monthly range per environment and in total, in whole dollars.
 * The free tier is counted once across all environments (it is per billing
 * account), used up by the environments in order. Returns null for clouds
 * without a price table yet.
 */
export function estimateStackCost(tier, cloud) {
    if (cloud.provider !== "gcp")
        return null;
    const p = STACK_PRICING.gcp;
    const settings = stackEnvSettings(tier);
    const lowFree = { ...p.freeTier };
    const highFree = { ...p.freeTier };
    const environments = settings.map((s) => ({
        environment: s.environment,
        low: Math.round(cost(lowUsage(), lowFree, p)),
        high: Math.round(cost(highUsage(s.maxInstances), highFree, p)),
    }));
    const total = environments.reduce((t, e) => ({ low: t.low + e.low, high: t.high + e.high }), { low: 0, high: 0 });
    const busyHours = Math.round(MONTH_SECONDS * HIGH_BUSY_SHARE / 3600);
    return {
        currency: p.currency,
        pricesAsOf: p.pricesAsOf,
        calculatorUrl: p.calculatorUrl,
        environments,
        total,
        assumptions: [
            `List prices for Google Cloud as of ${p.pricesAsOf}, for a Tier 1 region; ${cloud.region} may cost more if it is a Tier 2 region.`,
            `Each instance has ${String(INSTANCE_VCPU)} vCPU and ${String(INSTANCE_GIB * 1024)} MiB of memory and is billed only while handling requests.`,
            `Low: light traffic — about ${LOW_REQUESTS.toLocaleString("en-US")} requests a month per environment, ${String(LOW_REQUEST_SECONDS * 1000)} ms each; the app scales to zero when idle.`,
            `High: every allowed instance (${settings.map((s) => `${s.environment} ${String(s.maxInstances)}`).join(", ")}) busy for ${String(HIGH_BUSY_SHARE * 100)}% of the month (about ${String(busyHours)} hours), serving ${String(HIGH_REQUESTS_PER_SECOND)} requests a second each.`,
            `Google's monthly free tier (${p.freeTier.vcpuSeconds.toLocaleString("en-US")} vCPU-seconds, ${p.freeTier.gibSeconds.toLocaleString("en-US")} GiB-seconds, ${(p.freeTier.requests / 1_000_000).toLocaleString("en-US")} million requests) is counted once for the whole stack.`,
            `Includes ${String(LOW_IMAGES_GB)}–${String(HIGH_IMAGES_GB)} GB of stored container images per environment; databases, networking and other services you add are not included.`,
        ],
    };
}
//# sourceMappingURL=stack-cost.js.map